import { Phone, Clock, AlertTriangle, CheckCircle, XCircle } from "lucide-react";
import { IRSNotice } from "@/data/irsNotices";
import { Button } from "@/components/ui/button";
import franklinSofi from "@/assets/franklin-sofi.webp";

interface NoticeResultCardProps {
  notice: IRSNotice;
  phoneNumber: string;
  onReset: () => void;
}

const urgencyConfig = {
  low: {
    label: "Low Urgency",
    bgColor: "bg-green-50",
    textColor: "text-green-700",
    borderColor: "border-green-200",
    icon: CheckCircle,
  },
  medium: {
    label: "Moderate Urgency",
    bgColor: "bg-yellow-50",
    textColor: "text-yellow-700",
    borderColor: "border-yellow-300",
    icon: Clock,
  },
  high: {
    label: "High Urgency",
    bgColor: "bg-orange-50",
    textColor: "text-orange-700",
    borderColor: "border-orange-300",
    icon: AlertTriangle,
  },
  critical: {
    label: "Critical - Act Now",
    bgColor: "bg-red-50",
    textColor: "text-red-700",
    borderColor: "border-red-300",
    icon: XCircle,
  },
};

const NoticeResultCard = ({ notice, phoneNumber, onReset }: NoticeResultCardProps) => {
  const urgency = urgencyConfig[notice.urgency];
  const UrgencyIcon = urgency.icon;

  const handleCall = () => {
    window.location.href = `tel:${phoneNumber.replace(/[^0-9]/g, "")}`;
  };

  return (
    <div className="space-y-6">
      {/* Header with Notice Info */}
      <div className={`rounded-xl p-6 ${urgency.bgColor} border ${urgency.borderColor}`}>
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <UrgencyIcon className={`w-5 h-5 ${urgency.textColor}`} />
              <span className={`text-sm font-medium ${urgency.textColor}`}>
                {urgency.label}
              </span>
            </div>
            <h3 className="text-2xl font-heading font-bold text-navy-dark">
              {notice.number}
            </h3>
            <p className="text-lg text-navy-dark/80">{notice.name}</p>
          </div>
          {notice.deadline && (
            <div className="text-right">
              <p className="text-sm text-muted-foreground">Response Deadline</p>
              <p className={`text-xl font-bold ${urgency.textColor}`}>
                {notice.deadline}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* What This Means */}
      <div className="bg-card rounded-xl p-6 border border-border">
        <h4 className="font-heading font-semibold text-navy-dark mb-3">
          What This Notice Means
        </h4>
        <p className="text-muted-foreground leading-relaxed">
          {notice.description}
        </p>
      </div>

      {/* Consequences */}
      <div className="bg-card rounded-xl p-6 border border-border">
        <h4 className="font-heading font-semibold text-navy-dark mb-3">
          What Happens If You Ignore It
        </h4>
        <p className="text-muted-foreground leading-relaxed">
          {notice.consequences}
        </p>
      </div>

      {/* What To Do */}
      <div className="bg-navy-dark rounded-xl p-6 text-white">
        <div className="flex items-center gap-4 mb-4">
          <img 
            src={franklinSofi} 
            alt="Franklin Sofi, CPA - Ready for your call" 
            className="w-16 h-16 rounded-full object-cover border-3 border-gold shadow-lg"
           width={80} height={80} />
          <div>
            <p className="font-semibold text-white">Franklin Sofi, CPA</p>
            <p className="text-white/60 text-sm">Tax Resolution Specialist</p>
            <p className="text-gold text-sm mt-1">Let's discuss your {notice.number}</p>
          </div>
        </div>
        <h4 className="font-heading font-semibold mb-3">
          What You Should Do
        </h4>
        <p className="text-white/80 leading-relaxed mb-6">
          {notice.whatToDo}
        </p>
        <div className="flex flex-col sm:flex-row gap-3">
          <Button
            onClick={handleCall}
            className="flex-1 bg-gold hover:bg-gold-dark text-white font-semibold py-6"
          >
            <Phone className="w-5 h-5 mr-2" />
            Call Now: {phoneNumber}
          </Button>
          <Button
            onClick={onReset}
            variant="outline"
            className="border-white text-white bg-transparent hover:bg-white/10"
          >
            Look Up Another Notice
          </Button>
        </div>
      </div>
    </div>
  );
};

export default NoticeResultCard;
